import { Dumbbell, Footprints, PersonStanding } from 'lucide-react';
import React from 'react';
import { FlagBadge } from './FlagBadge';
import type {
    ActivityOverviewSupportDto,
    ActivityFlagSummaryDto,
    DailyStepsEntryDto,
    WorkoutEntryDto,
    FacilityVisitEntryDto,
    WorkoutGpxAnalysisSummaryDto
} from '../../../../core/api/FitnessActivityClient';
import { parseOffsetMinutes } from '../utils/timezoneOffset.utils';

function gpxAnalysisDetails(
    analysis?: WorkoutGpxAnalysisSummaryDto
): Record<string, string> {
    if (!analysis) return {};

    const details: Record<string, string> = {
        'GPX Verdict (shadow)': `${analysis.action} — ${String(analysis.result?.reason ?? '')}`
    };
    Object.entries(analysis.result ?? {})
        .filter(([key]) => key !== 'reason')
        .forEach(([key, value]) => {
            details[`GPX ${key}`] =
                typeof value === 'object' && value !== null
                    ? JSON.stringify(value)
                    : String(value);
        });
    return details;
}

export interface ActivityItem {
    id: number;
    type: 'workout' | 'dailySteps' | 'facilityVisit';
    label: string;
    timeRange?: string;
    details: Record<string, string>;
    flag?: ActivityFlagSummaryDto;
}

interface ActivityListProps {
    data: ActivityOverviewSupportDto | null;
    onActivityClick: (activity: ActivityItem) => void;
}

function formatTimeRange(from: string, to: string): string {
    const offsetMs = parseOffsetMinutes(from) * 60_000;
    const fmt = (iso: string) => {
        const d = new Date(new Date(iso).getTime() + offsetMs);
        return d.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'UTC'
        });
    };
    return `${fmt(from)} – ${fmt(to)}`;
}

function buildActivityItems(data: ActivityOverviewSupportDto): ActivityItem[] {
    const flags = data.activityFlags ?? {};
    const items: ActivityItem[] = [];

    data.dailySteps?.forEach((ds: DailyStepsEntryDto) => {
        items.push({
            id: ds.id,
            type: 'dailySteps',
            label: `Daily Steps: ${ds.stepCount.toLocaleString()} steps`,
            timeRange: ds.chunks?.length
                ? formatTimeRange(
                      ds.chunks[0].timeRange.from,
                      ds.chunks[ds.chunks.length - 1].timeRange.to
                  )
                : undefined,
            details: {
                'Step Count': ds.stepCount.toLocaleString(),
                'Fitness App': ds.fitnessApp ?? 'Unknown',
                'Activity Points': String(ds.activityPoints),
                ...(ds.rejected ? { Status: 'Rejected' } : {})
            },
            flag: flags[String(ds.id)]
        });
    });

    data.workouts?.forEach((w: WorkoutEntryDto) => {
        items.push({
            id: w.id,
            type: 'workout',
            label: `${w.workoutType} (${w.workoutCategory})`,
            timeRange: w.workoutDateTimeRange
                ? formatTimeRange(
                      w.workoutDateTimeRange.from,
                      w.workoutDateTimeRange.to
                  )
                : undefined,
            details: {
                Type: w.workoutType,
                Category: w.workoutCategory,
                ...(w.activityRejectionReason
                    ? { Rejection: w.activityRejectionReason }
                    : {}),
                ...gpxAnalysisDetails(w.gpxAnalysis)
            },
            flag: flags[String(w.id)]
        });
    });

    data.facilityVisits?.forEach((fv: FacilityVisitEntryDto) => {
        items.push({
            id: fv.id,
            type: 'facilityVisit',
            label: fv.facilityName ?? `Facility Visit`,
            timeRange: fv.facilityVisitEntryDateTimeRange
                ? formatTimeRange(
                      fv.facilityVisitEntryDateTimeRange.from,
                      fv.facilityVisitEntryDateTimeRange.to
                  )
                : undefined,
            details: {
                Facility: fv.facilityName ?? 'Unknown',
                'Visit Type': fv.visitType
            },
            flag: flags[String(fv.id)]
        });
    });

    return items;
}

const typeIcons: Record<ActivityItem['type'], React.ReactElement> = {
    dailySteps: <Footprints className="size-5 text-primary" />,
    workout: <PersonStanding className="size-5 text-primary" />,
    facilityVisit: <Dumbbell className="size-5 text-primary" />
};

export function ActivityList({ data, onActivityClick }: ActivityListProps) {
    if (!data) return null;

    const items = buildActivityItems(data);

    if (items.length === 0) return null;

    return (
        <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Activities ({items.length})
            </p>
            <ul className="flex flex-col gap-2">
                {items.map((item) => (
                    <li key={`${item.type}-${item.id}`}>
                        <button
                            type="button"
                            onClick={() => onActivityClick(item)}
                            className="flex w-full items-center gap-3 rounded-md border border-border border-l-[3px] border-l-primary/40 bg-muted/30 px-3 py-2 text-left transition-all hover:translate-x-0.5 hover:border-l-primary hover:bg-primary/10"
                        >
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                                {typeIcons[item.type]}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[0.8rem] font-medium">
                                    {item.label}
                                </span>
                                {item.timeRange && (
                                    <span className="text-[0.7rem] text-muted-foreground">
                                        {item.timeRange}
                                    </span>
                                )}
                            </span>
                            {item.flag && (
                                <FlagBadge status={item.flag.status} />
                            )}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
